/* Purchase · Suppliers — supplier master.
 * List + search + a slide-in drawer to add or edit a supplier.
 * Reuses supplyService.getSuppliers / createSupplier / updateSupplier
 * (GET / POST / PATCH v1/suppliers). Follows the Supply Caliper UI kit. */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import supplyService from "../../Services/supplyService";
import { getResolvedWarehouseCode } from "../../Services/warehouseContext";
import { useWarehouse } from "../Layout/WarehouseContext";
import {
  Button,
  Pill,
  Input,
  Table,
  Tr,
  Td,
  SectionHeader,
  ToolbarBar,
  Eyebrow,
  LoadingState,
  ErrorState,
  InlineAlert,
  IconPlus,
  IconX,
  IconCheck,
  IconChevRight,
  IconUser,
  emailError,
  phoneError,
} from "./supplyKit";
import { WarehouseSelect } from "./warehousePicker";
import { verifyGstin, gstinFormatError, normalizeGstin } from "./gstVerifyService";

const fieldInput = {
  width: "100%",
  height: 30,
  padding: "0 8px",
  border: "1px solid var(--border-default)",
  borderRadius: 4,
  fontSize: 12,
  fontFamily: "var(--font-sans)",
  background: "var(--bg-panel)",
  color: "var(--fg-default)",
  outline: "none",
};
const fieldLabel = {
  display: "block",
  fontSize: 10,
  fontWeight: 600,
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  color: "var(--fg-muted)",
  marginBottom: 4,
};

const Field = ({ label, children }) => (
  <div>
    <label style={fieldLabel}>{label}</label>
    {children}
  </div>
);

const blankSupplier = () => ({
  /* Required — defaults to the logged-in warehouse; operator can re-assign. */
  warehouseId: getResolvedWarehouseCode() || "",
  name: "",
  gstin: "",
  contactPerson: "",
  phone: "",
  email: "",
  drugLicense: "",
  address: "",
  pincode: "",
  city: "",
  state: "",
  creditDays: 0,
  isActive: true,
});

/* Look up an Indian 6-digit PIN code -> { city, state } via India Post.
   Returns null if not found / unreachable. */
const lookupPincode = async (pin, signal) => {
  const res = await fetch(`https://api.postalpincode.in/pincode/${pin}`, { signal });
  const json = await res.json();
  const entry = Array.isArray(json) ? json[0] : null;
  if (!entry || entry.Status !== "Success" || !entry.PostOffice?.length) return null;
  const po = entry.PostOffice[0];
  return { city: po.District || "", state: po.State || "" };
};

const Suppliers = () => {
  /* Scoped to the store selected app-wide (topbar), for every role — a head
     switches store there rather than seeing every store's suppliers at once. */
  const { code: wh } = useWarehouse();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [suppliers, setSuppliers] = useState([]);
  const [q, setQ] = useState("");
  const [showDrawer, setShowDrawer] = useState(false);
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    /* Only this store's suppliers — see the app-wide selection above. */
    supplyService
      .getSuppliers({
        warehouseId: wh || undefined,
      })
      .then((list) => {
        if (!alive) return;
        setSuppliers(list || []);
        setLoading(false);
      })
      .catch((e) => {
        if (!alive) return;
        setError(e?.message || "Could not load suppliers.");
        setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [reloadKey, wh]);

  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return suppliers;
    return suppliers.filter((s) =>
      [s.name, s.gstin, s.contactPerson, s.phone, s.email, s.city, s.state]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(needle)
    );
  }, [suppliers, q]);

  const openNew = () => {
    setEditing(null);
    setShowDrawer(true);
  };
  const openEdit = (s) => {
    setEditing(s);
    setShowDrawer(true);
  };

  return (
    <div className="cb-fade-in" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <SectionHeader
        title="Suppliers"
        sub="Supplier master · used by Purchase Orders and GRN receipts"
        actions={
          <Button variant="primary" icon={<IconPlus size={14} />} onClick={openNew}>
            New supplier
          </Button>
        }
      />

      <ToolbarBar>
        <Input
          placeholder="Search name / GSTIN / contact / city…"
          style={{ width: 320, maxWidth: "100%" }}
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <div style={{ marginLeft: "auto", fontSize: 11, color: "var(--fg-muted)", fontFamily: "var(--font-mono)" }}>
          {rows.length} supplier{rows.length !== 1 ? "s" : ""}
        </div>
      </ToolbarBar>

      {loading ? (
        <LoadingState label="Loading suppliers…" />
      ) : error ? (
        <ErrorState message={error} onRetry={() => setReloadKey((k) => k + 1)} />
      ) : (
        <Table
          columns={[
            { label: "Supplier" },
            { label: "GSTIN" },
            { label: "Contact" },
            { label: "Phone" },
            { label: "City / State" },
            { label: "Status" },
            { label: "", width: 24 },
          ]}
        >
          {rows.map((s) => (
            <Tr key={s.id} onClick={() => openEdit(s)}>
              <Td style={{ fontWeight: 600, color: "var(--fg-strong)" }}>
                {s.name}
                {s.drugLicense && (
                  <div style={{ fontFamily: "var(--font-mono)", fontSize: 10, color: "var(--fg-muted)" }}>
                    DL {s.drugLicense}
                  </div>
                )}
              </Td>
              <Td mono>{s.gstin || "—"}</Td>
              <Td>{s.contactPerson || "—"}</Td>
              <Td mono>{s.phone || "—"}</Td>
              <Td>{[s.city, s.state].filter(Boolean).join(", ") || "—"}</Td>
              <Td>
                <Pill variant={s.isActive === false ? "neutral" : "green"} dot>
                  {s.isActive === false ? "inactive" : "active"}
                </Pill>
              </Td>
              <Td>
                <IconChevRight size={12} style={{ color: "var(--fg-muted)" }} />
              </Td>
            </Tr>
          ))}
          {rows.length === 0 && (
            <Tr>
              <Td colSpan={7} style={{ textAlign: "center", color: "var(--fg-muted)", padding: 20 }}>
                No suppliers{q ? " match your search" : " yet"}.
              </Td>
            </Tr>
          )}
        </Table>
      )}

      {showDrawer && (
        <SupplierDrawer
          editing={editing}
          onClose={() => {
            setShowDrawer(false);
            setEditing(null);
          }}
          onSaved={() => {
            setReloadKey((k) => k + 1);
            setShowDrawer(false);
            setEditing(null);
          }}
        />
      )}
    </div>
  );
};

/* ---------------- Add / edit drawer ---------------- */
const SupplierDrawer = ({ editing, onClose, onSaved }) => {
  const isEditing = Boolean(editing?.id);
  const [form, setForm] = useState(() => (isEditing ? { ...blankSupplier(), ...editing } : blankSupplier()));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const [warehouses, setWarehouses] = useState([]);

  const setF = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  /* Warehouse options for the required scoping selector. */
  useEffect(() => {
    let alive = true;
    supplyService.getWarehouses().then((list) => {
      if (alive) setWarehouses(list || []);
    });
    return () => {
      alive = false;
    };
  }, []);

  const [pinLookup, setPinLookup] = useState({ status: "idle" }); // idle | loading | done | notfound | error
  const lastPinRef = useRef(null);

  /* GSTIN verification — the onboarding gate.
     idle | checking | verified | rejected | unavailable
     "rejected" is the register answering "no such GSTIN"; "unavailable" is the
     call not landing at all. They read differently and are treated differently:
     the first is the supplier's problem, the second is ours. */
  const [gst, setGst] = useState({ status: "idle" });
  /* An edit only has to re-verify if the GSTIN itself changed — an existing
     supplier is not re-onboarded every time someone fixes a phone number. */
  const savedGstin = useRef(normalizeGstin(editing?.gstin || ""));
  const gstVerifyCtrl = useRef(null);

  const gstUnchanged =
    isEditing && !!savedGstin.current && normalizeGstin(form.gstin) === savedGstin.current;
  const gstVerified = gst.status === "verified" || gstUnchanged;

  const handleVerifyGst = () => {
    const formatErr = gstinFormatError(form.gstin);
    if (formatErr) {
      setGst({ status: "rejected", message: formatErr });
      return;
    }
    gstVerifyCtrl.current?.abort();
    const ctrl = new AbortController();
    gstVerifyCtrl.current = ctrl;
    setGst({ status: "checking" });
    verifyGstin(form.gstin, { signal: ctrl.signal })
      .then((res) => {
        if (ctrl.signal.aborted) return;
        if (!res.ok) {
          setGst({ status: "rejected", message: res.message });
          return;
        }
        setGst({ status: "verified", data: res });
        /* Fill from the register, but never overwrite what the operator typed:
           a name they chose is a decision, and the register is only a source for
           what is still blank. */
        setForm((f) => {
          const next = { ...f, gstin: res.gstin };
          if (!f.name.trim()) next.name = res.tradeName || res.legalName || f.name;
          if (!f.address.trim()) next.address = res.address.full;
          if (!f.pincode.trim()) next.pincode = res.address.pincode;
          if (!f.city.trim()) next.city = res.address.city;
          if (!f.state.trim()) next.state = res.address.state;
          return next;
        });
        /* The PIN we just filled came with its own city and state, so stop the
           India Post lookup from firing and overwriting them. */
        if (res.address.pincode) lastPinRef.current = res.address.pincode;
      })
      .catch((e) => {
        if (ctrl.signal.aborted || e.name === "AbortError") return;
        setGst({
          status: "unavailable",
          message: e?.message || "GST verification is unreachable right now.",
        });
      });
  };


  // Auto-fill city/state when a full 6-digit PIN is entered.
  useEffect(() => {
    const pin = (form.pincode || "").trim();
    if (pin.length !== 6) {
      setPinLookup({ status: "idle" });
      return;
    }
    if (lastPinRef.current === pin) return; // already resolved this PIN
    lastPinRef.current = pin;

    const ctrl = new AbortController();
    setPinLookup({ status: "loading" });
    lookupPincode(pin, ctrl.signal)
      .then((loc) => {
        if (ctrl.signal.aborted) return;
        if (!loc) {
          setPinLookup({ status: "notfound" });
          return;
        }
        setForm((f) => ({ ...f, city: loc.city, state: loc.state }));
        setPinLookup({ status: "done" });
      })
      .catch((e) => {
        if (ctrl.signal.aborted || e.name === "AbortError") return;
        setPinLookup({ status: "error" });
      });
    return () => ctrl.abort();
  }, [form.pincode]);

  const phoneErr = phoneError(form.phone);
  const emailErr = emailError(form.email);

  /* GST verification is part of the gate, not a nicety: a supplier is not
     onboarded against a GSTIN nobody has checked. An edit that leaves the GSTIN
     alone keeps the verification it was onboarded with. */
  const valid =
    form.name.trim().length > 0 && !!form.warehouseId && !phoneErr && !emailErr && gstVerified;

  const handleSave = () => {
    if (!valid) return;
    setSaving(true);
    setErr(null);
    const call = isEditing
      ? supplyService.updateSupplier(editing.id, form)
      : supplyService.createSupplier(form);
    call
      .then(() => {
        toast.success(`Supplier ${isEditing ? "updated" : "added"} — ${form.name}.`);
        onSaved?.();
      })
      .catch((e) => {
        setSaving(false);
        setErr(e?.response?.data?.message || e?.message || "Could not save the supplier.");
      });
  };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 60 }}>
      <div onClick={onClose} style={{ position: "absolute", inset: 0, background: "var(--bg-overlay)" }} />
      <div
        style={{
          position: "absolute",
          top: 0,
          right: 0,
          bottom: 0,
          width: "min(560px, 96vw)",
          background: "var(--bg-canvas)",
          boxShadow: "-12px 0 40px rgba(13,17,27,0.18)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
        className="cb-animate-in"
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            padding: "12px 20px",
            borderBottom: "1px solid var(--border-default)",
            background: "var(--bg-panel)",
          }}
        >
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: 6,
              background: "var(--ink-50)",
              color: "var(--ink-700)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <IconUser size={18} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: "var(--fg-strong)" }}>
              {isEditing ? `Edit ${editing.name}` : "New supplier"}
            </div>
            <div style={{ fontSize: 11, color: "var(--fg-muted)", marginTop: 2 }}>Supplier master record</div>
          </div>
          <button
            onClick={onClose}
            style={{
              border: 0,
              background: "transparent",
              cursor: "pointer",
              color: "var(--fg-muted)",
              width: 28,
              height: 28,
              borderRadius: 4,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <IconX size={16} />
          </button>
        </div>

        <div style={{ flex: 1, overflow: "auto", padding: "16px 20px" }} className="cb-scroll">
          <Eyebrow style={{ display: "block", marginBottom: 10 }}>Supplier details</Eyebrow>
          <div className="grid grid-cols-1 sm:grid-cols-2" style={{ display: "grid", gap: 12 }}>
            <div style={{ gridColumn: "1 / -1" }}>
              <Field label="Warehouse *">
                {/* Defaults to the logged-in user's warehouse. Only a pharmacy
                    head (admin) sees other warehouses and can reassign; a plain
                    PH user sees ONLY their own warehouse and is locked to it. */}
                {/* Searchable for a head (10+ similarly-named warehouses); a
                    static label for a PH user, who has only their own. */}
                <WarehouseSelect
                  value={form.warehouseId || ""}
                  onChange={(v) => setF("warehouseId", v)}
                  warehouses={warehouses}
                  noneLabel="— Select warehouse —"
                  /* Always the store in scope: records are created in the store
                     you are working in, which the topbar names. To file one under
                     another store, switch store there. */
                  disabled
                  width="100%"
                />
              </Field>
            </div>
            <div style={{ gridColumn: "1 / -1" }}>
              <Field label="Name *">
                <input value={form.name} onChange={(e) => setF("name", e.target.value)} placeholder="Supplier name" style={fieldInput} />
              </Field>
            </div>
            <div style={{ gridColumn: "1 / -1" }}>
              <Field label="GSTIN * — verified against the GST register">
                <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                  <input
                    value={form.gstin}
                    onChange={(e) => {
                      setF("gstin", normalizeGstin(e.target.value));
                      /* Editing the number invalidates the verification — the
                         badge must never outlive the value it was about. */
                      setGst({ status: "idle" });
                    }}
                    placeholder="22AAAAA0000A1Z5"
                    maxLength={15}
                    style={{ ...fieldInput, fontFamily: "var(--font-mono)", textTransform: "uppercase", width: 200, maxWidth: "100%" }}
                  />
                  <Button
                    onClick={handleVerifyGst}
                    disabled={gst.status === "checking" || form.gstin.length !== 15 || gstUnchanged}
                  >
                    {gst.status === "checking" ? "Verifying…" : "Verify"}
                  </Button>
                  {gstVerified && (
                    <Pill variant="green" dot>
                      {gstUnchanged && gst.status !== "verified" ? "Verified on file" : "Verified"}
                    </Pill>
                  )}
                </div>

                {gst.status === "verified" && gst.data && (
                  <div style={{ fontSize: 11, color: "var(--fg-muted)", marginTop: 6, lineHeight: 1.5 }}>
                    <span style={{ color: "var(--fg-strong)", fontWeight: 600 }}>
                      {gst.data.legalName || gst.data.tradeName}
                    </span>
                    {gst.data.tradeName && gst.data.legalName && gst.data.tradeName !== gst.data.legalName
                      ? ` · trading as ${gst.data.tradeName}`
                      : ""}
                    <br />
                    {[gst.data.constitution, gst.data.taxpayerType, gst.data.registeredOn && `registered ${gst.data.registeredOn}`]
                      .filter(Boolean)
                      .join(" · ")}
                  </div>
                )}

                {/* A cancelled or suspended registration verifies perfectly well.
                    That is exactly when someone needs to be told. */}
                {gst.status === "verified" && gst.data && !/^active$/i.test(gst.data.status) && (
                  <div style={{ marginTop: 6 }}>
                    <InlineAlert tone="amber">
                      This GSTIN is <strong>{gst.data.status || "not active"}</strong>
                      {gst.data.cancelledOn ? ` (cancelled ${gst.data.cancelledOn})` : ""}. It verifies,
                      but check whether this supplier should be onboarded at all.
                    </InlineAlert>
                  </div>
                )}

                {gst.status === "rejected" && (
                  <div style={{ fontSize: 11, color: "var(--red-700)", marginTop: 6 }}>
                    {gst.message} — the supplier cannot be onboarded on this number.
                  </div>
                )}

                {gst.status === "unavailable" && (
                  <div style={{ marginTop: 6 }}>
                    <InlineAlert tone="amber">
                      {gst.message} Onboarding stays blocked until the check succeeds — the number has
                      not been confirmed either way. Try again in a moment.
                    </InlineAlert>
                  </div>
                )}

                {gst.status === "idle" && !gstUnchanged && (
                  <div style={{ fontSize: 10, color: "var(--fg-muted)", marginTop: 6 }}>
                    Verify the GSTIN to enable onboarding. Name and address fill in from the register.
                  </div>
                )}
              </Field>
            </div>
            <Field label="Drug licence">
              {/* 255, widened from 50 backend-side: distributors quote their 20B
                  and 21B numbers together and the field was truncating. */}
              <input
                value={form.drugLicense}
                onChange={(e) => setF("drugLicense", e.target.value)}
                maxLength={255}
                title="Up to 255 characters — both 20B and 21B numbers fit"
                style={{ ...fieldInput, fontFamily: "var(--font-mono)" }}
              />
            </Field>
            <Field label="Contact person">
              <input value={form.contactPerson} onChange={(e) => setF("contactPerson", e.target.value)} style={fieldInput} />
            </Field>
            <Field label="Phone">
              <input
                value={form.phone}
                onChange={(e) => setF("phone", e.target.value.replace(/[^\d]/g, "").slice(0, 10))}
                inputMode="numeric"
                maxLength={10}
                placeholder="9876543210"
                style={{
                  ...fieldInput,
                  fontFamily: "var(--font-mono)",
                  border: phoneErr ? "1px solid var(--red-500, #d92d20)" : fieldInput.border,
                }}
              />
              {phoneErr && (
                <div style={{ fontSize: 10, color: "var(--red-700, #b42318)", marginTop: 4 }}>{phoneErr}</div>
              )}
            </Field>
            <div style={{ gridColumn: "1 / -1" }}>
              <Field label="Email">
                <input
                  value={form.email}
                  onChange={(e) => setF("email", e.target.value)}
                  type="email"
                  placeholder="orders@supplier.in"
                  style={{
                    ...fieldInput,
                    border: emailErr ? "1px solid var(--red-500, #d92d20)" : fieldInput.border,
                  }}
                />
                {emailErr && (
                  <div style={{ fontSize: 10, color: "var(--red-700, #b42318)", marginTop: 4 }}>{emailErr}</div>
                )}
              </Field>
            </div>
            <div style={{ gridColumn: "1 / -1" }}>
              <Field label="Address">
                <textarea
                  value={form.address}
                  onChange={(e) => setF("address", e.target.value)}
                  rows={2}
                  placeholder="Street, area, landmark"
                  style={{ ...fieldInput, height: "auto", resize: "vertical", fontFamily: "var(--font-sans)" }}
                />
              </Field>
            </div>
            <div style={{ gridColumn: "1 / -1" }}>
              <Field label="PIN code">
                <input
                  value={form.pincode}
                  onChange={(e) => setF("pincode", e.target.value.replace(/[^\d]/g, "").slice(0, 6))}
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="751024 — auto-fills city & state"
                  style={{ ...fieldInput, fontFamily: "var(--font-mono)", width: 220, maxWidth: "100%" }}
                />
                {pinLookup.status === "loading" && (
                  <div style={{ fontSize: 10, color: "var(--fg-muted)", marginTop: 4 }}>Looking up…</div>
                )}
                {pinLookup.status === "notfound" && (
                  <div style={{ fontSize: 10, color: "var(--amber-700, #b54708)", marginTop: 4 }}>
                    PIN not found — enter city & state manually.
                  </div>
                )}
                {pinLookup.status === "error" && (
                  <div style={{ fontSize: 10, color: "var(--amber-700, #b54708)", marginTop: 4 }}>
                    Lookup unavailable — enter city & state manually.
                  </div>
                )}
              </Field>
            </div>
            <Field label="City">
              <input value={form.city} onChange={(e) => setF("city", e.target.value)} style={fieldInput} />
            </Field>
            <Field label="State">
              <input value={form.state} onChange={(e) => setF("state", e.target.value)} style={fieldInput} />
            </Field>
            <Field label="Credit days">
              <input
                value={form.creditDays}
                onChange={(e) => setF("creditDays", e.target.value.replace(/[^\d]/g, "").slice(0, 4))}
                inputMode="numeric"
                placeholder="e.g. 30"
                style={{ ...fieldInput, fontFamily: "var(--font-mono)" }}
              />
            </Field>
            <Field label="Status">
              <select
                value={form.isActive === false ? "inactive" : "active"}
                onChange={(e) => setF("isActive", e.target.value === "active")}
                style={fieldInput}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </Field>
          </div>

          {err && (
            <div style={{ marginTop: 12 }}>
              <InlineAlert>{err}</InlineAlert>
            </div>
          )}
        </div>

        <div
          style={{
            display: "flex",
            gap: 8,
            alignItems: "center",
            flexWrap: "wrap",
            padding: "12px 20px",
            borderTop: "1px solid var(--border-default)",
            background: "var(--bg-panel)",
          }}
        >
          <Button onClick={onClose}>Cancel</Button>
          <div style={{ flex: 1 }} />
          <span style={{ fontSize: 11, color: valid ? "var(--green-700)" : "var(--fg-muted)", fontFamily: "var(--font-mono)" }}>
            {valid
              ? "Ready to save"
              : !form.warehouseId
              ? "Warehouse required"
              : !form.name.trim()
              ? "Supplier name required"
              : !gstVerified
              ? "GST verification required"
              : "Check the highlighted fields"}
          </span>
          <Button variant="primary" icon={<IconCheck size={14} />} disabled={!valid || saving} onClick={handleSave}>
            {saving ? "Saving…" : isEditing ? "Save changes" : "Add supplier"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Suppliers;
